
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref, toRefs, watch } from 'vue'
import ProductSearch from '@/pages/Products/components/ProductSearch.vue'
import ProductSearchModal from '@/pages/Products/components/ProductSearchModal.vue'
import { CtCategory, CtProduct, CtStockItem } from '@/models/interfaces/content-type'
import { useStore } from 'vuex'
import ProductsList from '@/pages/Products/components/ProductsList.vue'
import { UUID, generateUUID } from '@Protium/vue-ozone-utils'
import { debounce } from 'lodash'
import { useRouter } from 'vue-router'
import { ContentType, DirectrEvent } from '@/services/directr'
import { useQuasar } from 'quasar'

const MAX_RECENT_ITEMS = 3

export default defineComponent({
  name: 'ProductsOverview',
  props: {
    page: {
      type: Number,
      required: true,
      default: 1,
    },
    pageSize: {
      type: Number,
      required: true,
      default: 12,
    }
  },
  components: { ProductsList, ProductSearch, ProductSearchModal },

  setup: function (props) {
    const $q = useQuasar()
    const $store = useStore()
    const $router = useRouter()
    const $directr = $store.getters['Services/Directr']
    const recentlyVisitedProducts = ref<string[]>([])

    const state = reactive({
      searchValue: '',
      filterQuery: '',
      categoryId: '',
      brand: '',
      currPage: (props.page ?? 1),
      recentProducts: [],
    })

    const products = ref<CtProduct[]>([])
    const productTotal = ref<number | undefined>(undefined)

    const pageCount = computed(
      () => productTotal.value !== undefined ? Math.ceil(productTotal.value / props.pageSize) : undefined
    )

    const categories = ref<CtCategory[]>([])
    const selectedCategories = ref<CtCategory['sys']['id'][]>([])
    const categorySearch = ref<string | null>(null)
    const brands = ref<CtStockItem[]>([])
    const selectedBrands = ref<CtStockItem['brand'][]>([])
    const brandSearch = ref<string | null>(null)

    const loading = ref(false)
    const error = ref(null)

    onMounted(() => {
      $directr.listen(DirectrEvent['Create/Product'], (event) => {
        if (!event?.payload) return
        const productIndex = products.value?.findIndex(p => p.sys.id === event.payload?.sys?.id)
        if (productIndex < 0) products.value.unshift(event.payload)
      })
      $directr.listen(DirectrEvent['Update/Product'], (event) => {
        if (!event?.payload) return
        const productIndex = products.value?.findIndex(p => p.sys.id === event.payload?.sys?.id)
        if (productIndex < 0) return
        else if (event.payload?.sys?.id
          && products.value[productIndex].sys.updated_at
          && (new Date(event.payload.sys.updated_at).valueOf() > new Date(products.value[productIndex].sys.updated_at as any).valueOf())
        ) products.value.splice(productIndex, 1, {...products.value[productIndex],...event.payload})
      })
    })

    const fetchProducts = async () => {
      try {
        loading.value = true
        // console.log((state.filterQuery?.length <= 1 && !(selectedCategories.value?.length || selectedBrands.value?.length)))
        if (state.filterQuery?.length <= 1 && !(selectedCategories.value?.length || selectedBrands.value?.length)) {
          const { sys, items } = await $directr.get(ContentType.Product, [{ select: ['title', 'components', 'sku', 'ean', 'is_parent',] }, { field: '__created_at', method: 'exists' }], { limit: props.pageSize, skip: (state.currPage - 1) * props.pageSize })
          productTotal.value = sys.total
          products.value = items
          return
        }

        const { items } = await $directr.product.searchProps(state.filterQuery, selectedCategories.value, selectedBrands.value)
        // if (rejected?.length) console.warn('Search partially failed', rejected)

        productTotal.value = items.length
        products.value = items

      } catch (e: any) {
        error.value = e
      } finally {
        loading.value = false
      }
    }

    const fetchFilters = async () => {
      await Promise.all([
        $directr.get(ContentType.Category, [{
          where: {
            and: [
              { type: { in: ['product', 'stock'] } },
              (selectedCategories.value?.length ? { not: [{ __id: { in: selectedCategories.value } }] } : undefined),
              (categorySearch.value?.length ? {
                or: [
                  { ilike: ['name', categorySearch.value] },
                  { ilike: ['label', categorySearch.value] }
                ]
              } : undefined)
            ].filter(Boolean)
          },
          orderBy: { name: 'ASC' },
          limit: 20 - selectedCategories.value.length,
        }], { orm: true }).then(({ items = [] }) => {
          categories.value = categories.value.filter(c => selectedCategories.value.includes(c.sys.id)).concat(items)
        }),
        $directr.get(ContentType.StockItem, [{
          distinct: ['brand'],
          select: ['__id', 'brand'],
          where: {
            and: [
              (selectedBrands.value?.length ? { not: [{ brand: { in: selectedBrands.value } }] } : undefined),
              (brandSearch.value?.length ? { ilike: ['brand', brandSearch.value] } : undefined)
            ].filter(Boolean)
          },
          orderBy: { brand: 'ASC' },
          limit: 20 - selectedBrands.value.length,
        }], { orm: true }).then(({ items = [] }) => {
          brands.value = brands.value.filter(b => selectedBrands.value.includes(b.brand)).concat(items)
        }),
      ])
    }


    const setPage = async (newNum) => {
      state.currPage = newNum
      await $router.push({ query: { page: newNum, pageSize: props.pageSize !== 24 ? props.pageSize : undefined } })
    }

    watch(() => state.filterQuery, async () => {
      void fetchProducts()
      await setPage(1)
      if (state.filterQuery !== '' && state.filterQuery !== null) {
        void $router.push({
          query: {
            ...$router.currentRoute.value.query,
            search: state.filterQuery,
          }
        })
      }
      else {
        //emptying the router query when the search is cleared
        void $router.push({ query: {} })
      }
    })

    watch(() => state.currPage, fetchProducts)
    watch(() => [...selectedCategories.value, selectedBrands.value], () => {
      void setPage(1)
      void fetchProducts()
    })

    watch(() => [categorySearch.value, brandSearch.value], () => {
      void fetchFilters()
    })

    const addProduct = () => {
      const UUID = generateUUID()
      void $router.push({
        name: 'ProductDetailIndex',
        params: {
          productId: UUID,
          creating: 'true',
        },
      })
    }

    const setRecentlyVisitedProducts = (event: string): void => {
      if (!event || (recentlyVisitedProducts.value.includes(event))) return

      recentlyVisitedProducts.value.unshift(event)
      recentlyVisitedProducts.value.splice(MAX_RECENT_ITEMS)

      localStorage.setItem('recentlyVisitedProducts', JSON.stringify(recentlyVisitedProducts.value))
    }

    const getRecentlyVisitedProducts = async () => {
      const storedVisitedProducts = localStorage.getItem('recentlyVisitedProducts')
      if (storedVisitedProducts) {
        recentlyVisitedProducts.value = JSON.parse(storedVisitedProducts)

        // enforce adjusted max recent items if changed
        if (recentlyVisitedProducts.value?.length)
          recentlyVisitedProducts.value = recentlyVisitedProducts.value.slice(0, MAX_RECENT_ITEMS)
      }
      // SELECT __id,title FROM product WHERE __id IN recentlyVisitedProducts.value
      if (recentlyVisitedProducts.value?.length && Array.isArray(recentlyVisitedProducts.value)) {
        const { items } = await $directr.get(ContentType.Product, [{
          select: ['__id', 'title'],
          from: ContentType.Product,
          where: {
            __id: { in: recentlyVisitedProducts.value }
          }
        }], { orm: true })
        state.recentProducts = items
      }
    }

    function setFilter () {
      state.filterQuery = state.searchValue
    }

    watch(() => state.searchValue, debounce(setFilter, 100, { trailing: true }))

    const filteredProducts = computed(() => {
      return (Array.isArray(products.value) ? products.value : !products.value ? [] : [products.value]).filter((p,i,a) => a.findIndex(pn => pn.sys.id === p.sys.id) === i)
    })

    onMounted(() => {
      const routeQuery = $router.currentRoute.value.query

      const previousRouteInfo = localStorage.getItem('previousRouteInfo')
      localStorage.removeItem('previousRouteInfo')

      let query = {}

      if (previousRouteInfo && JSON.parse(previousRouteInfo).previousRouteName === 'ProductsOverview') {
        query = JSON.parse(previousRouteInfo).query
      } else {
        query = routeQuery
      }

      state.searchValue = query && query['search'] ? query['search'] : ''
      state.filterQuery = query && query['search'] ? query['search'] : ''
      state.currPage = query && query['page'] ? parseInt(query['page']) : 1

      void getRecentlyVisitedProducts()
      void fetchFilters()
      void fetchProducts()
    })

    function navigateToProduct (sysId: UUID) {
      if (!sysId) return
      void $router.push({ name: 'ProductDetailIndex', params: { productId: sysId, s: 'barcode', editing: '' } })
    }

    // watch(() => selectedCategories.value, (newValue, oldValue) => {
    //   console.log(oldValue, newValue)
    // })

    return {
      ...toRefs(state),
      loading,
      pageCount,
      products,
      productTotal,
      categories,
      selectedCategories,
      categorySearch,
      brands,
      selectedBrands,
      brandSearch,
      addProduct,
      filteredProducts,
      setRecentlyVisitedProducts,
      recentlyVisitedProducts,
      setPage,
      navigateToProduct
    }
  }
})
